import { useState, useEffect } from 'react';
import './Giveaway.css';
import './TwitchTimers.css'; // Reusing timer styles

export default function TwitchCommands() {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [showAdd, setShowAdd] = useState(false);
  const [newTrigger, setNewTrigger] = useState('');
  const [newMessage, setNewMessage] = useState('');

  const [twitchUsername, setTwitchUsername] = useState(localStorage.getItem('twitch_username') || '');

  const loadCommands = async () => {
    try {
      const token = localStorage.getItem('discord_token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-commands`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCommands(data);
      }
    } catch (e) {
      console.error(e);
      setError('Fehler beim Laden der Commands.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCommands();
  }, []);

  const handleAddCommand = async (e) => {
    e.preventDefault();
    if (!newTrigger.trim() || !newMessage.trim()) return;
    if (!twitchUsername) {
      setError('Bitte verknüpfe zuerst deinen Twitch-Account in der Account Info Seite.');
      return;
    }

    try {
      const token = localStorage.getItem('discord_token');
      const res = await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-commands`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          channel: twitchUsername,
          command_trigger: newTrigger.startsWith('!') ? newTrigger : `!${newTrigger}`,
          response_message: newMessage
        })
      });

      if (res.ok) {
        setNewTrigger('');
        setNewMessage('');
        setShowAdd(false);
        loadCommands();
      } else {
        const data = await res.json();
        setError(data.error || 'Fehler beim Erstellen.');
      }
    } catch (e) {
      console.error(e);
      setError('Fehler beim Erstellen.');
    }
  };

  const handleToggleCommand = async (id, currentActive) => {
    try {
      const token = localStorage.getItem('discord_token');
      await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-commands/${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !currentActive })
      });
      loadCommands();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteCommand = async (id) => {
    if (!window.confirm('Command wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('discord_token');
      await fetch(`${process.env.REACT_APP_API_BASE || '/api'}/bot-commands/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      loadCommands();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="page twitch-timers-page">
      <h1>⚡ Twitch Commands</h1>
      <p className="muted" style={{ marginBottom: '20px' }}>
        Lege eigene Befehle fest. Wenn ein Zuschauer im Chat den Befehl tippt, antwortet der Bot automatisch mit deinem Text.
      </p>

      {!twitchUsername && (
        <div className="giveaway-twitch-not-connected" style={{ marginBottom: '20px' }}>
          <p>🔑 Bitte verbinde zuerst deinen Twitch-Account unter "Account Info", bevor du Commands anlegst.</p>
        </div>
      )}

      {error && <div className="giveaway-twitch-error" style={{ marginBottom: '20px' }}>⚠️ {error}</div>}

      <div className="timers-header">
        <button 
          className={showAdd ? "giveaway-btn-end" : "giveaway-btn-create"}
          onClick={() => setShowAdd(!showAdd)}
          disabled={!twitchUsername}
        >
          {showAdd ? 'Abbrechen' : '+ Neuer Command'}
        </button>
      </div>

      {showAdd && (
        <div className="giveaway-twitch-card new-timer-card">
          <h3>Neuen Command anlegen</h3>
          <form onSubmit={handleAddCommand}>
            <div className="form-group">
              <label>Befehl (Auslöser)</label>
              <input 
                type="text"
                className="timers-input"
                placeholder="z.B. !dc oder !socials"
                value={newTrigger}
                onChange={(e) => setNewTrigger(e.target.value.replace(/\s+/g, ''))}
                maxLength={30}
                required
              />
            </div>
            <div className="form-group">
              <label>Antwort-Text</label>
              <textarea 
                className="timers-textarea"
                placeholder="z.B. Kommt auf meinen Discord Server: https://discord.gg/..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                maxLength={450}
                required
              />
            </div>
            <button type="submit" className="giveaway-btn-create" disabled={!newTrigger.trim() || !newMessage.trim()}>
              Speichern
            </button>
          </form>
        </div>
      )}

      {loading ? (
        <p className="muted">Lade Commands...</p>
      ) : commands.length === 0 ? (
        <div className="giveaway-twitch-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <p className="muted">Du hast noch keine Commands angelegt.</p>
        </div>
      ) : (
        <div className="timers-list">
          {commands.map(cmd => (
            <div key={cmd.id} className={`giveaway-twitch-card timer-card ${cmd.is_active ? 'active-timer' : 'inactive-timer'}`}>
              <div className="timer-content">
                <p className="timer-message">"{cmd.response_message}"</p>
                <div className="timer-meta">
                  <span className="timer-badge">⚡ {cmd.command_trigger}</span>
                  <span className="timer-badge">📍 #{cmd.channel}</span>
                </div>
              </div>
              <div className="timer-actions">
                <button 
                  className={cmd.is_active ? 'giveaway-btn-end' : 'giveaway-btn-create'}
                  onClick={() => handleToggleCommand(cmd.id, cmd.is_active)}
                >
                  {cmd.is_active ? 'Deaktivieren' : 'Aktivieren'}
                </button>
                <button 
                  className="giveaway-btn-end"
                  onClick={() => handleDeleteCommand(cmd.id)}
                  style={{ marginLeft: '10px' }}
                >
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
